/* Fetch helper for both SPAs. The staff app talks to /api/v1, the portal to /portal/api/v1; both
   send the corecrm_csrf cookie back as X-CSRF-Token on unsafe requests (double submit), read
   errors as RFC 9457 problem+json, and use the record's `version` as its ETag for If-Match. */

import { IdempotencyKey, newIdempotencyKey } from "./idempotency";

export { IdempotencyKey, newIdempotencyKey, useIdempotencyKey } from "./idempotency";

export interface FieldError {
  location: string[];
  message: string;
  type?: string;
}

export interface Problem {
  type?: string;
  title?: string;
  status: number;
  code: string;
  detail?: string | null;
  request_id?: string;
  errors?: FieldError[];
  [extension: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail || problem.title || problem.code);
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }

  /** Validation messages keyed by field name (the last element of each error's `loc`). */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) {
      const key = e.location[e.location.length - 1] ?? "";
      if (key && !out[key]) out[key] = e.message.replace(/^Value error, /, "");
    }
    return out;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined | (string | number)[]>;

export interface RequestOptions {
  query?: Query;
  /** The record's version; sent as If-Match: "<version>". */
  version?: number;
  /** Send an Idempotency-Key: a fresh one for `true`, the given one for a string, or a form's
      IdempotencyKey, which keeps its key across retries of the same request. */
  idempotent?: boolean | string | IdempotencyKey;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiOptions {
  baseUrl: string;
  /** Called when the session is missing or expired (401 `unauthenticated`), before the error is
      thrown; each app redirects to its own sign-in. Wrong passwords and codes don't call it. */
  onUnauthenticated?: (error: ApiError) => void;
  /** Called after a DELETE that put something in the recycle bin, with the bin entries it made
      (the X-Deletion-Batch header), so the app can offer Undo. */
  onDeleted?: (batchIds: string[]) => void;
  fetch?: typeof fetch;
}

export const DELETION_BATCH_HEADER = "X-Deletion-Batch";

export const CSRF_COOKIE = "corecrm_csrf";

export function readCookie(name: string, cookie = typeof document === "undefined" ? "" : document.cookie) {
  for (const part of cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

export function buildQuery(query?: Query): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, String(x)));
    else p.append(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function toProblem(res: Response): Promise<Problem> {
  const text = await res.text().catch(() => "");
  try {
    const body = JSON.parse(text) as Partial<Problem>;
    if (body && typeof body === "object" && "code" in body) return { ...body, status: res.status } as Problem;
  } catch {
    /* not JSON */
  }
  return {
    status: res.status,
    code: res.status === 0 ? "network" : `http_${res.status}`,
    title: res.statusText || "Request failed",
  };
}

export function createApi({ baseUrl, onUnauthenticated, onDeleted, fetch: fetchImpl }: ApiOptions) {
  const base = baseUrl.replace(/\/$/, "");

  async function send(
    method: string,
    path: string,
    body: unknown,
    opts: RequestOptions = {},
    accept = "application/json",
    retried = false,
  ): Promise<Response> {
    const headers: Record<string, string> = { Accept: accept, ...opts.headers };
    const raw = body instanceof FormData || body instanceof Blob;
    if (body !== undefined && !raw) headers["Content-Type"] = "application/json";
    const csrf = UNSAFE.has(method) ? readCookie(CSRF_COOKIE) : null;
    if (csrf) headers["X-CSRF-Token"] = csrf;
    if (opts.version !== undefined) headers["If-Match"] = `"${opts.version}"`;
    const scope = opts.idempotent instanceof IdempotencyKey ? opts.idempotent : null;
    if (scope) headers["Idempotency-Key"] = raw ? newIdempotencyKey() : scope.for([method, path, body]);
    else if (opts.idempotent)
      headers["Idempotency-Key"] =
        typeof opts.idempotent === "string" ? opts.idempotent : newIdempotencyKey();

    let res: Response;
    try {
      res = await (fetchImpl ?? fetch)(base + path + buildQuery(opts.query), {
        method,
        headers,
        credentials: "same-origin",
        body: body === undefined ? undefined : raw ? (body as BodyInit) : JSON.stringify(body),
        signal: opts.signal,
      });
    } catch (e) {
      if ((e as Error)?.name === "AbortError") throw e;
      scope?.settle(0);
      throw new ApiError({
        status: 0,
        code: "network",
        title: "Can't reach CoreCRM. Check your connection.",
      });
    }
    if (!res.ok) {
      const err = new ApiError(await toProblem(res));
      scope?.settle(res.status, err.code);
      // A fresh browser has no CSRF cookie until the API sets one, which it does on the refusal.
      if (err.code === "csrf_failed" && !csrf && !retried && readCookie(CSRF_COOKIE))
        return send(method, path, body, opts, accept, true);
      if (res.status === 401 && err.code === "unauthenticated") onUnauthenticated?.(err);
      throw err;
    }
    scope?.settle(res.status);
    const batches = method === "DELETE" ? res.headers.get(DELETION_BATCH_HEADER) : null;
    if (batches && onDeleted) onDeleted(batches.split(",").filter(Boolean));
    return res;
  }

  async function json<T>(method: string, path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    const res = await send(method, path, body, opts);
    if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  return {
    baseUrl: base,
    get: <T>(path: string, opts?: RequestOptions) => json<T>("GET", path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) => json<T>("POST", path, body ?? {}, opts),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) => json<T>("PUT", path, body ?? {}, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      json<T>("PATCH", path, body ?? {}, opts),
    del: <T = void>(path: string, opts?: RequestOptions) => json<T>("DELETE", path, undefined, opts),
    /** Download a file (PDF, CSV, deliverable) with the session cookie. */
    blob: async (path: string, opts?: RequestOptions & { accept?: string }) => {
      const res = await send("GET", path, undefined, opts, opts?.accept ?? "*/*");
      const cd = res.headers.get("content-disposition") ?? "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      return { blob: await res.blob(), filename: m?.[1] ? decodeURIComponent(m[1]) : null };
    },
    /** Upload multipart form data. */
    upload: <T>(path: string, form: FormData, opts?: RequestOptions) => json<T>("POST", path, form, opts),
    /** Upload one file as the raw request body, its name URL-encoded in X-Filename (deliverable files). */
    uploadFile: <T>(path: string, file: File, opts?: RequestOptions) =>
      json<T>("POST", path, file, {
        ...opts,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "X-Filename": encodeURIComponent(file.name),
          ...opts?.headers,
        },
      }),
  };
}

export type Api = ReturnType<typeof createApi>;

/** Save a blob through a temporary link (the browser's download). */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
